import React, { useEffect, useState } from 'react';
import AuthContext from './AuthContext';
import axios from 'axios';
import {
  auth,
  firebaseApplyActionCode,
  firebaseCheckActionCode,
  firebaseLogin,
  firebaseLoginWithAzurePopup,
  firebaseLogout,
  firebaseResetPassword,
  firebaseSendEmailVerification,
  firebaseSendPasswordResetEmail,
  firebaseSignUp,
} from './AuthenticationService';
import { trackerService } from '../../api/tracker';

function AuthState({ children }) {
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isSupportModalOpen, setSupportModalOpen] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userCountryCode, _setUserCountryCode] = useState('ca');
  const [userInfo, setUserInfo] = useState(undefined);
  const setLoadingCounter = useState(0)[1]; // just need the setter

  ///////////
  // Loading

  const incrementLoading = () => {
    setLoadingCounter((prev) => {
      const count = prev + 1;
      if (count > 0) {
        setIsLoading(true);
      }
      return count;
    });
  };

  const decrementLoading = () => {
    setLoadingCounter((prev) => {
      const count = prev - 1;
      if (count <= 0) {
        setIsLoading(false);
      }
      return count;
    });
  };

  /////////////////
  // Notifications

  // Add a notification
  const addNotification = (
    message,
    severity = 'info',
    action = null,
    timeout = 15000
  ) => {
    // If an action is provided, it should be an object with a text and onClick function
    // eg: action = {
    //    onClick: () => console.log('clicked'),
    //    text: 'Click Me',
    // }
    // If no action is provided, an "X" button will be displayed to close the notification
    const id = Math.random(); // Simple unique ID generation
    setNotifications((notifications) => [
      ...notifications,
      {
        id,
        message,
        severity,
        action,
        timeout,
      },
    ]);
  };

  // Remove all notifications
  const clearNotifications = () => {
    setNotifications([]);
  };

  // Remove a notification
  const removeNotification = (id) => {
    setNotifications((notifications) =>
      notifications.filter((notification) => notification.id !== id)
    );
  };

  //////////////////
  // Core functions

  const getUserInfo = async (firebaseUser) => {
    // Internal function to just get the userInfo
    incrementLoading();
    try {
      const token = await firebaseUser.getIdToken();
      const { data } = await axios.post(
        '/get_user_info',
        {
          countryCode: userCountryCode,
        },
        {
          headers: { Authorization: token },
        }
      );
      let countryCode = data.preferred_country_code;
      delete data.preferred_country_code;
      setUserInfo(data);
      _setUserCountryCode(countryCode);
      return data;
    } catch (error) {
      // propagate the error
      throw error;
    } finally {
      decrementLoading();
    }
  };

  const isSubscribedTo = (moduleName) => {
    /* allowed moduleNames =
     * [sub_crm, sub_cross_md, sub_loc_int, sub_persona, sub_mmm, any]
     *  */

    // step (1) Check expiration date
    if (!userInfo?.expiration) return false;
    let parsed_date = Date.parse(userInfo.expiration);
    if (isNaN(parsed_date)) {
      // Date.parse fails on some browsers (safari) using default date format,
      // so we try converting it to a format that works
      parsed_date = Date.parse(
        userInfo.expiration.replace(/-/g, '/').replace(/[a-z]+/gi, ' ')
      );
    }

    // step (2) check subscription
    if (new Date(parsed_date) >= new Date()) {
      // not expired, step (1) pass
      if (moduleName === 'any') {
        const { sub_crm, sub_cross_md, sub_loc_int, sub_persona, sub_mmm } =
          userInfo;
        return sub_crm + sub_cross_md + sub_loc_int + sub_persona + sub_mmm > 0;
      } else {
        return userInfo[moduleName] > 0;
      }
    }

    return false;
  };

  const login = async (email, password) => {
    incrementLoading();
    try {
      const { firebaseUser } = await firebaseLogin(email, password);
      return firebaseUser;
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const logout = async () => {
    incrementLoading();
    try {
      await firebaseLogout();
      clearNotifications();
      addNotification('Logged out successfully.', 'info');
      setUserInfo(null);
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const loginWithAzure = async (oobCode = false) => {
    incrementLoading();
    try {
      const { firebaseUser, additionalUserInfo } =
        await firebaseLoginWithAzurePopup();
      // Does user need to be registered? (new user)
      if (additionalUserInfo.isNewUser) {
        await registerUserDatastore(
          firebaseUser,
          'microsoft',
          userCountryCode,
          oobCode
        );
        insertHubspotContact(firebaseUser.email, 'registration-azure');
        await getUserInfo(firebaseUser); // get updated user info
      }
      return firebaseUser;
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 401) {
        // user already exists, invalid domain, etc. || 401 invalid oobCode
        addNotification(
          error.response.data?.status || 'Internal server error',
          'error'
        );
      } else if (error.isFirebaseError) {
        // Known firebase error - closed popup, blocked popup, etc.
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const signup = async (
    email,
    password,
    name,
    countryValue,
    oobCode = false
  ) => {
    incrementLoading();
    try {
      // clean up email and convert to lowercase before signup
      email = email.trim().toLowerCase();
      // signup user with firebase
      const { firebaseUser } = await firebaseSignUp(email, password);
      await firebaseUser.updateProfile({ displayName: name });
      // register with datastore backend
      const data = await registerUserDatastore(
        firebaseUser,
        'email',
        countryValue,
        oobCode
      );

      // user is verified after registering with invitation link
      if (data.verified) {
        addNotification(
          'Congratulations! Your registration is complete, ' +
            'your email is verified, and roles are assigned. Please sign in ' +
            'to begin exploring your new account.',
          'success'
        );
        await insertHubspotContact(email, 'registration-invitation');
      }

      // user is not verified yet
      else {
        await sendVerificationEmail(
          firebaseUser,
          'Registration Successful! Please check your ' +
            'registered email for email verification.'
        ); // hubspot contact will be added after email verification
        addNotification(
          'Did not get an email?',
          'info',
          {
            onClick: async () => {
              await sendVerificationEmail(firebaseUser);
            },
            text: 'Request another verification email.',
          },
          false // no timeout, keep on screen until logs in, leaves, refresh, or clicks
        );
      }
      return data.verified;
    } catch (error) {
      if (error.response?.status === 400) {
        const errorText =
          error.response.data?.status ||
          'Unable to register with this email address. Please reach out for assistance.';

        const notificationText = (
          <>
            {`${errorText} You can also `}
            <span
              onClick={() => setSupportModalOpen(true)}
              style={{
                color: 'blue',
                cursor: 'pointer',
              }}
            >
              click here
            </span>
            {' to fill out our contact form.'}
          </>
        );
        addNotification(notificationText, 'error');
      }
      if (error.response?.status === 401) {
        // user already exists, invalid domain, etc. || 401 invalid oobCode
        addNotification(
          error.response.data?.status || 'Internal server error',
          'error'
        );
      } else if (error.isFirebaseError) {
        // Known firebase error - weak password, email already in use, etc.
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const setUserCountryCode = async (code) => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        await axios.post(
          '/edit_user_settings',
          { attribute: 'preferred_country_code', value: code },
          { headers: { Authorization: token } }
        );
      } catch {}
    }
    _setUserCountryCode(code);
  };

  const setUserSetting = async (key, value) => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        await axios.post(
          '/edit_user_settings',
          { attribute: key, value: value },
          { headers: { Authorization: token } }
        );
      } catch (error) {
        console.error('Failed to set user setting:', error);
      }
    }
  };

  const resetPassword = async (oobCode, newPassword) => {
    // reset password
    incrementLoading();
    try {
      const userEmail = await firebaseResetPassword(oobCode, newPassword);
      addNotification(
        "Password updated successfully! You're all set to " +
          'log in with your new password.',
        'success'
      );
      return userEmail;
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const sendVerificationEmail = async (firebaseUser, successMsg = null) => {
    incrementLoading();
    try {
      await firebaseSendEmailVerification(firebaseUser);
      addNotification(
        successMsg ??
          'Verification email sent! Please check your email for further instructions.',
        'success'
      );
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const updateProfile = async (profile) => {
    await firebaseUser.updateProfile(profile);
    await getUserInfo(firebaseUser);
  };

  const verifyOOBCode = async (oobCode, successMsg) => {
    incrementLoading();
    try {
      let userEmail = await firebaseCheckActionCode(oobCode);
      await firebaseApplyActionCode(oobCode);
      if (firebaseUser) {
        firebaseUser.reload();
      }
      addNotification(successMsg, 'success');
      insertHubspotContact(userEmail, 'registration-email');
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  const sendPasswordResetEmail = async (email) => {
    incrementLoading();
    try {
      await firebaseSendPasswordResetEmail(email);
      addNotification(
        'Password reset email sent! Please check your email for further instructions.',
        'success'
      );
    } catch (error) {
      if (error.isFirebaseError) {
        addNotification(error.message, error.type);
      } else {
        // unknown error -- let the error catcher handle it
        throw error;
      }
    } finally {
      decrementLoading();
    }
  };

  /////////////////////
  // Support functions

  const insertHubspotContact = async (email, source = 'registration') => {
    // add contact to hubspot
    incrementLoading();
    try {
      await axios.post('/new_contact', {
        email: email,
        emailSource: source,
      });
    } catch (err) {
      void 0; // backend will record failed contact insertions
    } finally {
      decrementLoading();
    }
  };

  const registerUserDatastore = async (
    firebaseUser,
    idProvider,
    countryValue,
    oobCode = false
  ) => {
    // helper function used to register a firebase user to datastore
    incrementLoading();
    try {
      const token = await firebaseUser.getIdToken();
      const { data } = await axios.post(
        '/user_register',
        {
          name: firebaseUser.displayName,
          idProvider: idProvider,
          country: countryValue,
          oobCode: oobCode,
        },
        {
          headers: { Authorization: token },
        }
      );
      return data;
    } catch (error) {
      // propagate the error
      throw error;
    } finally {
      decrementLoading();
    }
  };

  const getIdToken = () => {
    if (firebaseUser) {
      return firebaseUser.getIdToken();
    } else {
      return undefined;
    }
  };

  useEffect(() => {
    // listen for auth status changes. On mount and on change
    auth.onAuthStateChanged(async (firebaseUser) => {
      //////////////////////////////////////////////////////////////////////////
      // sign in / sign up
      if (firebaseUser) {
        if (firebaseUser.emailVerified === true) {
          //////////////////////////////////////////////////////////////////////
          // emailVerified means that this is a sign-in post-registration
          // SSO login get verified post datastore entry and regular sign up post
          // email verification -- both cases are finalised users.
          setFirebaseUser(firebaseUser);
          try {
            const userInfoCopy = await getUserInfo(firebaseUser);
            trackerService.captureEvent('Login', userInfoCopy, {
              $set: {
                last_active_date: trackerService.getTimestamp(),
              },
              $set_once: {
                registration_date: trackerService.getTimestamp(), // if already set, this won't overwrite
              },
            });
          } catch (error) {
            if (
              error.response?.status === 404 ||
              error.response?.status === 403
            ) {
              // If you get a 404 not found or 403 forbidden, the user does not have
              // a datastore entry. Delete the firebase user, log out, and inform the user
              addNotification(
                'We encountered an unexpected issue while setting up your account. Please try again.',
                'error'
              );
              await firebaseUser.delete();
            } else {
              // propagate the error
              throw error;
            }
          }
        } else if (
          firebaseUser.providerData?.[0]?.providerId === 'microsoft.com'
        ) {
          //////////////////////////////////////////////////////////////////////
          // Azure AD login are considered verified (backend will set the
          // emailVerified flag) unverified microsoft sign is a new registration.
          // The registration process has not been completed yet. Once completed,
          // it will call "getUserInfo" and set user as verified.
          setFirebaseUser(firebaseUser); // need the firebaseUser to complete the registration
        } else {
          //////////////////////////////////////////////////////////////////////
          // User is not verified and is not an Azure AD login
          // email verification is mandatory -- sign out user -- login page will advise user
          await firebaseLogout();
        }
      }
      //////////////////////////////////////////////////////////////////////////
      // sign-out
      else {
        trackerService.captureEvent('Logout', userInfo, {
          $set: {
            last_active_date: trackerService.getTimestamp(),
          },
        });
        setFirebaseUser(null);
        setUserInfo(null);
      }
    });
  }, []); // eslint-disable-line

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        notifications,
        firebaseUser,
        userCountryCode,
        userInfo,
        isSupportModalOpen,
        setSupportModalOpen,
        // notifications
        addNotification,
        clearNotifications,
        removeNotification,
        sendPasswordResetEmail,
        // core functions
        isSubscribedTo,
        login,
        logout,
        loginWithAzure,
        signup,
        setUserCountryCode,
        setUserSetting,
        resetPassword,
        sendVerificationEmail,
        updateProfile,
        verifyOOBCode,
        // support functions
        insertHubspotContact,
        getIdToken,
      }}
    >
      {/* Wait for firebase session to start --  undefined !== null  */}
      {/* undefined == session hasn't started. null == no logged in user */}
      {userInfo !== undefined && children}
    </AuthContext.Provider>
  );
}

export default AuthState;
